import { Box, Typography, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';

import QuestionSection from './QuestionSection';

import { useMember } from 'modules/common/queries/member';
import { getCurrentTaxYear } from 'modules/Tax/constants';
import { isSCorpMember } from 'utils/businessStructure';

export interface QuestionnaireAnswers {
    individualTaxReturn: 'yes' | 'no' | null;
    businessTaxReturn: 'yes' | 'no' | null;
    businessVehicle: 'yes' | 'no' | null;
    businessLoans: 'yes' | 'no' | null;
    accountingSoftware: 'yes' | 'no' | null;
}

interface UploadDocumentsQuestionnaireProps {
    initialAnswers?: Partial<QuestionnaireAnswers>;
    onAnswersChange: (answers: QuestionnaireAnswers) => void;
    isSaving?: boolean;
}

const defaultAnswers: QuestionnaireAnswers = {
    individualTaxReturn: null,
    businessTaxReturn: null,
    businessVehicle: null,
    businessLoans: null,
    accountingSoftware: null,
};

export default function UploadDocumentsQuestionnaire({
    initialAnswers = {},
    onAnswersChange,
    isSaving = false,
}: UploadDocumentsQuestionnaireProps) {
    const [answers, setAnswers] = useState<QuestionnaireAnswers>({
        ...defaultAnswers,
        ...initialAnswers,
    });
    const [isInitialized, setIsInitialized] = useState(false);
    const { data: member } = useMember();

    // Update internal state when initialAnswers prop changes (only when it has actual data)
    useEffect(() => {
        // Only update if initialAnswers has meaningful data (not just empty object)
        const hasData = Object.values(initialAnswers).some((value) => value !== null && value !== undefined);
        if (hasData) {
            setAnswers({
                ...defaultAnswers,
                ...initialAnswers,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        initialAnswers.individualTaxReturn,
        initialAnswers.businessTaxReturn,
        initialAnswers.businessVehicle,
        initialAnswers.businessLoans,
        initialAnswers.accountingSoftware,
    ]);

    // Notify parent of answer changes (but not on initial mount)
    useEffect(() => {
        if (isInitialized) {
            onAnswersChange(answers);
        } else {
            setIsInitialized(true);
        }
    }, [answers, onAnswersChange, isInitialized]);

    const handleQuestionChange = (questionId: string, value: string) => {
        setAnswers((prev) => ({
            ...prev,
            [questionId]: value as 'yes' | 'no',
        }));
    };

    // Helper function to check if member was formed in the current tax year
    const isFormedInCurrentTaxYear = () => {
        if (!member?.establisheddate) {
            return false;
        }
        // The calendar year, deliberately not getCurrentTaxYear(): a member formed this year has
        // no return for the year being filed, so the question is hidden. Swapping this for the tax
        // year would hide it from everyone formed during the filing year, who do have one.
        const currentCalendarYear = new Date().getFullYear();
        const formationYear = new Date(member.establisheddate).getFullYear();
        return formationYear === currentCalendarYear;
    };

    // Filter questions based on business structure and formation date
    const questionSections = [
        {
            title: 'Tax Filings',
            helpQuestion:
                'Why does my past tax filing history matter for setting up my books, and what happens if I get it wrong?',
            questions: [
                {
                    id: 'individualTaxReturn',
                    question: `Did you file your ${getCurrentTaxYear()} Individual Tax Return?`,
                    value: answers.individualTaxReturn,
                },
                // Only show business tax return question for S-Corp members who were formed before current tax year
                ...(isSCorpMember(member?.usertype || '') && !isFormedInCurrentTaxYear()
                    ? [
                          {
                              id: 'businessTaxReturn',
                              question: `Did you file your ${getCurrentTaxYear()} Business Tax Return?`,
                              value: answers.businessTaxReturn,
                          },
                      ]
                    : []),
            ],
        },
        {
            title: 'Assets and liabilities',
            helpQuestion:
                'Why do my business assets and loans matter for setting up my books, and what happens if I leave one out?',
            questions: [
                {
                    id: 'businessVehicle',
                    question: 'Do you have a vehicle dedicated 100% for business?',
                    value: answers.businessVehicle,
                },
                {
                    id: 'businessLoans',
                    question: `Did you take out any loans under ${member?.businessname}?`,
                    value: answers.businessLoans,
                },
            ],
        },
        {
            title: 'Systems and tools',
            helpQuestion: 'Why does my current accounting software and payroll setup matter for setting up my books?',
            questions: [
                {
                    id: 'accountingSoftware',
                    question: 'Are you using any accounting software to manage your bookkeeping right now?',
                    value: answers.accountingSoftware,
                },
            ],
        },
    ];

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 6, // 48px gap between title and content (matches Figma)
            }}>
            {/* Header */}
            <Box sx={{ textAlign: 'left' }}>
                <Typography variant="h1" color="text.primary" sx={{ mb: 1.5 }}>
                    Let&apos;s figure out which documents you need
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    We&apos;ll use your answers to determine which documents to collect so we can build clean,
                    audit-ready books.
                </Typography>
            </Box>

            {/* Saving Indicator */}
            {isSaving && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'center' }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                        Saving answers...
                    </Typography>
                </Box>
            )}

            {/* Question Sections */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {' '}
                {/* 32px gap between sections (matches Figma) */}
                {questionSections.map((section) => (
                    <QuestionSection
                        key={section.title}
                        title={section.title}
                        questions={section.questions}
                        onQuestionChange={handleQuestionChange}
                        helpQuestion={section.helpQuestion}
                    />
                ))}
            </Box>
        </Box>
    );
}
